import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { LinearProgress, Box } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';
import moment from 'moment';

import { DonationDisbursementCorporateGivingType } from 'generated/graphql';
import { selectUser } from 'entities/user/selectors';
import TabItem from 'retired/TabItem';
import { Privileges } from 'utils/privileges';

import { CreateInvoicePayroll } from './CreateInvoicePayroll';
import { CreateInvoiceCorporateGiving } from './CreateInvoiceCorporateGiving';
import { getRecommendedPaymentDueDateHelper } from './getRecommendedPaymentDueDateHelper';

const CreateInvoicePage = (): JSX.Element => {
  const user = useSelector(selectUser);
  const history = useHistory();

  const utcMidnight = moment().utc().startOf('day').toDate();

  const [dateTo, setDateTo] = useState<Date>(utcMidnight);

  if (user && !user.hasPrivilege(Privileges.RESTRICTED_INVOICING_MANAGE)) {
    history.replace('/');
  }

  const corporateGivingInvoicingEnabled =
    user &&
    Object.values(DonationDisbursementCorporateGivingType).some(
      (type) => user.organization.disbursementConfiguration?.invoicing[type]
    );

  const getRecommendedPaymentDueDate = () => {
    const today = new Date();
    const paymentDueDateConfig =
      user?.organization?.disbursementConfiguration?.invoiceSettings?.paymentDueDate;

    return getRecommendedPaymentDueDateHelper(today, paymentDueDateConfig);
  };

  if (!user) {
    return <LinearProgress />;
  }

  return (
    <>
      <Helmet title="Create Invoice" />
      <Box maxWidth={1280}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
          <Box>
            <TabItem isActive>Create Invoice</TabItem>
            <TabItem onClick={() => history.push('/invoices')}>Invoices</TabItem>
          </Box>
        </Box>

        <CreateInvoicePayroll getRecommendedPaymentDueDate={getRecommendedPaymentDueDate} />

        {corporateGivingInvoicingEnabled && (
          <>
            <h2>Corporate Giving</h2>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <DatePicker
                value={dateTo}
                onChange={(date) => {
                  if (date) {
                    // Create a UTC date at 0:00 GMT of the selected date
                    const utcDate = moment.utc(date).startOf('day').toDate();
                    setDateTo(utcDate);
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
                label="Disbursements Until"
              />
            </Box>
            {Object.values(DonationDisbursementCorporateGivingType).map((type) => (
              <CreateInvoiceCorporateGiving
                key={type}
                donationDisbursementType={type}
                dateTo={dateTo}
                getRecommendedPaymentDueDate={getRecommendedPaymentDueDate}
              />
            ))}
          </>
        )}
      </Box>
    </>
  );
};

export default CreateInvoicePage;
